import { useState, type ReactNode } from 'react';

import { api } from '../api';
import { Aviso, Cartao, Estado, Painel, data, pct, useAcao, useCarga } from '../comum';
import { AcoesOperacionais } from './AcoesOperacionais';

interface Sugestao {
  readonly id: string;
  readonly tipo: string;
  readonly alvoTipo: string;
  readonly alvoId: string;
  readonly payload: Record<string, unknown>;
  readonly confianca: string;
  readonly citacoes: readonly { readonly ticketNumero?: number; readonly trecho?: string }[];
  readonly estado: string;
  readonly criadaEm: string;
  readonly expiraEm: string;
}

interface Metricas {
  readonly geradas: number;
  readonly aceitas: number;
  readonly editadas: number;
  readonly rejeitadas: number;
  readonly taxaDeAceite: number | null;
}

interface Cobertura {
  readonly ticketsFechados: number;
  readonly comResolucao: number;
  readonly percentual: number | null;
}

/** Códigos internos viram português antes de chegar à tela. */
const ROTULO_TIPO: Readonly<Record<string, string>> = {
  triagem: 'Triagem de chamado',
  resolucao: 'Resolução sugerida',
  resumo: 'Resumo',
  incidente_massivo: 'Possível incidente massivo',
  viabilidade: 'Análise de viabilidade',
  plano_remediacao: 'Plano de remediação',
};

const ROTULO_ALVO: Readonly<Record<string, string>> = {
  ticket: 'chamado',
  dispositivo: 'máquina',
  regra: 'regra',
};

const ROTULO_CAMPO: Readonly<Record<string, string>> = {
  resolucao: 'Resolução',
  categoria: 'Categoria',
  prioridade: 'Prioridade',
  impacto: 'Impacto',
  titulo: 'Título',
  resumo: 'Resumo',
  justificativa: 'Justificativa',
  proximoPasso: 'Próximo passo',
  fila: 'Fila',
  responsavel: 'Responsável',
};

/**
 * A proposta, legível.
 *
 * Campos conhecidos viram linhas rotuladas; o que sobrar aparece como JSON,
 * porque esconder um campo que não sabemos nomear seria pior — a pessoa
 * aprovaria algo que a tela não mostrou.
 */
function Proposta({ payload }: { payload: Record<string, unknown> }): ReactNode {
  const entradas = Object.entries(payload);
  if (entradas.length === 0) return <p className="suave">Proposta vazia.</p>;

  const conhecidos = entradas.filter(([k]) => k in ROTULO_CAMPO);
  const resto = entradas.filter(([k]) => !(k in ROTULO_CAMPO));

  return (
    <div className="proposta">
      {conhecidos.map(([k, v]) => (
        <div key={k} className="proposta-campo">
          <span className="proposta-rotulo">{ROTULO_CAMPO[k]}</span>
          <span className="proposta-valor">
            {typeof v === 'string' ? v : JSON.stringify(v)}
          </span>
        </div>
      ))}
      {resto.length > 0 && (
        <details>
          <summary className="suave">
            Mais {resto.length} campo(s) sem nome conhecido
          </summary>
          <pre className="mono">{JSON.stringify(Object.fromEntries(resto), null, 2)}</pre>
        </details>
      )}
    </div>
  );
}

/**
 * Fila de aprovação da IA.
 *
 * Toda saída de IA entra aqui como **sugestão tipada**. Nada é escrito no KACE
 * antes de um humano decidir, e o autor da decisão vem da sessão — nunca de um
 * campo que a pessoa preenche.
 */
export function Aprovacoes({
  aoPerderSessao,
  podeDecidir,
}: {
  aoPerderSessao: () => void;
  podeDecidir: boolean;
}): ReactNode {
  const acao = useAcao(aoPerderSessao);
  const [motivo, setMotivo] = useState<Record<string, string>>({});
  /**
   * Qual sugestão está aberta para edição, e o rascunho.
   *
   * Uma por vez: abrir várias convida a aprovar a errada, e o campo do ajuste
   * exige atenção — é ele que vira o sinal de qualidade.
   */
  const [editando, setEditando] = useState<string | null>(null);
  const [rascunho, setRascunho] = useState<Record<string, string>>({});
  const [ajuste, setAjuste] = useState('');
  const [pagina, setPagina] = useState(1);

  const pendentes = useCarga<{
    itens: readonly Sugestao[];
    total: number;
    pagina: number;
    paginas: number;
    ocultasPorBaixaConfianca: number;
    confiancaMinima: number;
  }>(
    () => api.get(`/api/sugestoes?pagina=${pagina}`),
    [pagina],
    aoPerderSessao,
  );
  const metricas = useCarga<Metricas>(() => api.get('/api/ia/metricas'), [], aoPerderSessao);
  const cobertura = useCarga<Cobertura>(
    () => api.get('/api/conhecimento/cobertura'),
    [],
    aoPerderSessao,
  );
  /**
   * 🔴 Os nomes aqui são o contrato do servidor, não escolha desta tela.
   *
   * Estavam errados nos dois eixos — o campo era `decisao` (o servidor lê
   * `acao`) e os valores eram `aceita`/`rejeitada` (o servidor aceita
   * `aprovar`/`editar`/`rejeitar`). Todo clique caía no `default` e voltava
   * 400: **a tela inteira não aprovava nem rejeitava nada**.
   *
   * Um teste de contrato lê este arquivo e o da rota, e falha se voltarem a
   * divergir. Contrato entre camadas não se protege com atenção.
   */
  const decidir = (id: string, escolha: 'aprovar' | 'rejeitar'): void => {
    if (escolha === 'rejeitar' && (motivo[id] ?? '').trim() === '') {
      acao.falhar('Rejeitar exige motivo — é o que aponta onde melhorar o modelo.');
      return;
    }
    void acao.rodar(async () => {
      await api.post(`/api/sugestoes/${encodeURIComponent(id)}/decidir`, {
        acao: escolha,
        ...(escolha === 'rejeitar' ? { motivo: (motivo[id] ?? '').trim() } : {}),
      });
      // Decidir remove o item da fila. Se era o último da página, ficar nela
      // mostraria uma lista vazia com "página 3 de 2".
      if ((pendentes.dados?.itens.length ?? 0) <= 1 && pagina > 1) setPagina(pagina - 1);
      else pendentes.recarregar();
      metricas.recarregar();
      return escolha === 'aprovar' ? 'Sugestão aprovada.' : 'Sugestão rejeitada.';
    });
  };

  const abrirEdicao = (s: Sugestao): void => {
    setEditando(s.id);
    setAjuste('');
    // Só os campos com nome conhecido são editáveis: o que a tela não sabe
    // nomear ela também não sabe validar, e vai junto sem alteração.
    setRascunho(
      Object.fromEntries(
        Object.entries(s.payload)
          .filter(([k]) => k in ROTULO_CAMPO)
          .map(([k, v]) => [k, typeof v === 'string' ? v : JSON.stringify(v)]),
      ),
    );
  };

  const salvarEdicao = (s: Sugestao): void => {
    if (ajuste.trim() === '') {
      acao.falhar(
        'Descreva o que você corrigiu. É esse texto que mostra onde o modelo ' +
          'erra — sem ele, a edição não ensina nada.',
      );
      return;
    }
    void acao.rodar(async () => {
      await api.post(`/api/sugestoes/${encodeURIComponent(s.id)}/decidir`, {
        acao: 'editar',
        ajuste: ajuste.trim(),
        // O que a tela não editava volta como veio.
        payload: { ...s.payload, ...rascunho },
      });
      setEditando(null);
      if ((pendentes.dados?.itens.length ?? 0) <= 1 && pagina > 1) setPagina(pagina - 1);
      else pendentes.recarregar();
      metricas.recarregar();
      return 'Sugestão aprovada com ajuste.';
    });
  };

  return (
    <>
      {/*
        Ações no parque vêm antes das sugestões de IA de propósito: uma escreve
        no KACE, a outra propõe texto. Enterrar a que age abaixo da que sugere
        inverte a ordem de atenção de quem opera.
      */}
      <AcoesOperacionais aoPerderSessao={aoPerderSessao} podeDecidir={podeDecidir} />

      {acao.erro !== null && <Aviso tipo="erro">{acao.erro}</Aviso>}
      {acao.ok !== null && <Aviso tipo="ok">{acao.ok}</Aviso>}

      <Estado carga={metricas}>
        {metricas.dados !== null && (
          <div className="cartoes">
            <Cartao rotulo="Sugestões geradas" valor={metricas.dados.geradas} />
            <Cartao rotulo="Aceitas" valor={metricas.dados.aceitas} />
            <Cartao rotulo="Editadas" valor={metricas.dados.editadas} />
            <Cartao rotulo="Rejeitadas" valor={metricas.dados.rejeitadas} />
            <Cartao rotulo="Taxa de aceite" valor={pct(metricas.dados.taxaDeAceite)} />
          </div>
        )}
      </Estado>

      <Estado carga={cobertura}>
        {cobertura.dados !== null && (
          <Aviso tipo={(cobertura.dados.percentual ?? 0) < 0.3 ? 'alerta' : 'info'}>
            <strong>Base citável:</strong> {cobertura.dados.comResolucao} de{' '}
            {cobertura.dados.ticketsFechados} chamados fechados têm resolução escrita (
            {pct(cobertura.dados.percentual)}).{' '}
            {(cobertura.dados.percentual ?? 0) < 0.3 &&
              'Com pouca base, a IA tem pouco de onde citar — e sugestão sem fonte válida é descartada de propósito.'}
          </Aviso>
        )}
      </Estado>

      {pendentes.dados !== null && pendentes.dados.ocultasPorBaixaConfianca > 0 && (
        <Aviso tipo="info">
          {pendentes.dados.ocultasPorBaixaConfianca} sugestão(ões) não aparecem por
          confiança abaixo de {pct(pendentes.dados.confiancaMinima)}. Elas existem, mas
          o modelo não se comprometeu com elas — revisar palpite custa mais atenção do
          que economiza.
        </Aviso>
      )}

      <Painel
        titulo={
          pendentes.dados === null || pendentes.dados.total === 0
            ? 'Aguardando decisão'
            : `Aguardando decisão — ${pendentes.dados.total}`
        }
      >
        <Estado carga={pendentes}>
          {pendentes.dados !== null &&
            (pendentes.dados.itens.length === 0 ? (
              <p className="suave">Nada pendente.</p>
            ) : (
              pendentes.dados.itens.map((s) => (
                <div
                  key={s.id}
                  style={{
                    borderBottom: '1px solid var(--borda)',
                    paddingBottom: 16,
                    marginBottom: 16,
                  }}
                >
                  <div className="linha" style={{ marginBottom: 8 }}>
                    <div>
                      <strong>{ROTULO_TIPO[s.tipo] ?? s.tipo}</strong>{' '}
                      <span className="suave">
                        · {ROTULO_ALVO[s.alvoTipo] ?? s.alvoTipo} {s.alvoId} · confiança{' '}
                        {pct(Number(s.confianca))} · expira {data(s.expiraEm)}
                      </span>
                    </div>
                  </div>

                  {/*
                    🔴 O que se aprova precisa ser legível.
                    Era `JSON.stringify` cru. Quem aprova sem conseguir ler o
                    que está aprovando não está decidindo — está clicando, e a
                    fila de aprovação vira teatro.
                  */}
                  {editando === s.id ? (
                    <div className="proposta">
                      {Object.entries(rascunho).map(([k, v]) => (
                        <div key={k} className="proposta-campo">
                          <span className="proposta-rotulo">{ROTULO_CAMPO[k]}</span>
                          <textarea
                            className="proposta-editar"
                            value={v}
                            rows={v.length > 90 ? 4 : 1}
                            onChange={(e) =>
                              setRascunho({ ...rascunho, [k]: e.target.value })
                            }
                          />
                        </div>
                      ))}
                      <input
                        style={{ marginTop: 10, width: '100%' }}
                        placeholder="O que você corrigiu? (obrigatório)"
                        value={ajuste}
                        onChange={(e) => setAjuste(e.target.value)}
                      />
                      <div className="linha" style={{ marginTop: 8 }}>
                        <button
                          className="primario encolhe"
                          disabled={acao.executando}
                          onClick={() => salvarEdicao(s)}
                        >
                          Aprovar com ajuste
                        </button>
                        <button className="encolhe" onClick={() => setEditando(null)}>
                          Cancelar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <Proposta payload={s.payload} />
                  )}

                  {s.citacoes.length > 0 && (
                    <details>
                      <summary className="suave">
                        {s.citacoes.length} fonte(s) citada(s) do histórico do próprio
                        cliente
                      </summary>
                      <ul>
                        {s.citacoes.map((c, i) => (
                          <li key={i} className="suave">
                            {c.ticketNumero !== undefined && <strong>#{c.ticketNumero} </strong>}
                            {c.trecho ?? ''}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}

                  {podeDecidir && (
                    <div className="linha" style={{ marginTop: 12 }}>
                      <div>
                        <input
                          placeholder="Motivo (obrigatório para rejeitar)"
                          value={motivo[s.id] ?? ''}
                          onChange={(e) => setMotivo({ ...motivo, [s.id]: e.target.value })}
                        />
                      </div>
                      <button
                        className="primario encolhe"
                        disabled={acao.executando}
                        onClick={() => decidir(s.id, 'aprovar')}
                      >
                        Aprovar
                      </button>
                      {/*
                        Aprovar com ajuste era aceito pelo servidor e não tinha
                        botão: a métrica "Editadas" nunca saía de zero. O ajuste
                        é o sinal mais valioso para medir a qualidade do modelo —
                        aprovar e rejeitar dizem "sim" e "não"; só a edição diz
                        **o quê**.
                      */}
                      {editando !== s.id && (
                        <button className="encolhe" onClick={() => abrirEdicao(s)}>
                          Editar
                        </button>
                      )}
                      <button
                        className="perigo encolhe"
                        disabled={acao.executando}
                        onClick={() => decidir(s.id, 'rejeitar')}
                      >
                        Rejeitar
                      </button>
                    </div>
                  )}
                </div>
              ))
            ))}
        </Estado>

        {pendentes.dados !== null && pendentes.dados.paginas > 1 && (
          <div className="linha" style={{ marginTop: 12 }}>
            <button
              className="encolhe"
              disabled={pagina <= 1}
              onClick={() => setPagina(pagina - 1)}
            >
              ← Anterior
            </button>
            <span className="suave">
              Página {pendentes.dados.pagina} de {pendentes.dados.paginas}
            </span>
            <button
              className="encolhe"
              disabled={pagina >= pendentes.dados.paginas}
              onClick={() => setPagina(pagina + 1)}
            >
              Próxima →
            </button>
          </div>
        )}
      </Painel>
    </>
  );
}
